import { useEffect, useLayoutEffect, useState } from "react";

import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { Carousel, Embla } from "@mantine/carousel";
import { EmblaCarouselType } from "embla-carousel-react";

import { useSlideStyles } from "../../features/MintPage/header/MintCarouselCommon";
import MintCarouselSlide from "../../features/MintPage/header/MintCarouselSlide";

type Props = {
  images: string[];
  onSlideChange: (index: number) => void;
  getEmblaApi?: (embla: EmblaCarouselType) => void;
  fullHeight?: boolean;
};

export const StaticCarousel = ({
  images,
  onSlideChange,
  getEmblaApi,
  fullHeight,
}: Props) => {
  const { isMobile, isExtraLarge } = useMantineMediaQueries({
    withInitialValues: true,
  });
  const [smallestAspectRatio, setSmallestAspectRatio] = useState(1);
  const slideSize = isMobile
    ? 270
    : isExtraLarge
    ? smallestAspectRatio >= 1
      ? 600
      : 480
    : 360;

  const { classes } = useSlideStyles({
    selected: false,
    isMobile,
    imagesCount: images.length,
    fullHeight,
    slideSize,
  });

  const [startingIndex] = useState(
    images.length > 2 ? Math.floor(images.length / 2) - 1 : 0
  );
  const [selectedIndex, setSelectedIndex] = useState(startingIndex);
  const [emblaApi, setEmbla] = useState<Embla | null>(null);

  useLayoutEffect(() => {
    if (emblaApi && slideSize) {
      emblaApi.reInit();
    }
  }, [slideSize, emblaApi]);

  useEffect(() => {
    onSlideChange(selectedIndex);
  }, [onSlideChange, selectedIndex]);

  useEffect(() => {
    if (getEmblaApi && emblaApi) {
      getEmblaApi(emblaApi);
      emblaApi.scrollTo(startingIndex, true);
    }
  }, [getEmblaApi, emblaApi, startingIndex]);

  return (
    <Carousel
      initialSlide={startingIndex}
      getEmblaApi={setEmbla}
      align="center"
      slideSize={slideSize}
      w="100%"
      draggable
      withKeyboardEvents={false}
      withControls
      inViewThreshold={0}
      onSlideChange={setSelectedIndex}
      containScroll="keepSnaps"
      classNames={{
        viewport: classes.viewport,
        root: classes.root,
        slide: classes.slide,
        container: classes.container,
      }}
      style={{
        overflow: "hidden",
      }}
    >
      {images.map((image, index) => (
        <MintCarouselSlide
          key={index}
          imageUrl={image}
          selected={index === selectedIndex}
          minted={false}
          index={index}
          fullHeight={fullHeight}
          slideSize={slideSize}
          onAspectRatio={(aspectRatio) => {
            if (aspectRatio < smallestAspectRatio) {
              setSmallestAspectRatio(aspectRatio);
            }
          }}
        />
      ))}
    </Carousel>
  );
};
